/**
 * The leader stays visible behind near architecture (lifted from the shared
 * prototype engine's `engine/src/architecture-cutaway.js` with the options the
 * dungeon-floors prototype's `dungeon-surfaces.js` gives it). Geometry tagged
 * `lwCutaway` (timber frames and their iron bands) is discarded inside a small
 * screen-space ellipse around the leader when it stands between the camera
 * and the leader; a frame part whose header crosses the aperture collapses
 * whole with its header (`dqLink`: the header's centre and signed half
 * length), so a header never floats on either side of a hole. The aperture is
 * computed in projected coordinates, so it is the same in every render target.
 * Not lifted: the engine's `occlusion.js` (GTAO contact occlusion), which the
 * dungeon never used and which costs a pass the phone budget does not have.
 */
import * as THREE from 'three';
import { presentation } from './constants.ts';
import type { ShaderHook } from './material-hooks.ts';

const uniforms = {
  lwCutawayCenter: { value: new THREE.Vector4() },
  lwCutawayRadius: { value: new THREE.Vector2(1, 1) },
};

const collapse = `
dungeonCutClip=gl_Position;
lwEligible=lwCutaway;
lwViewDepth=-mvPosition.z;
#ifdef USE_INSTANCING
if(lwCutaway>.5&&lwCutawayCenter.w>.5){
  mat4 dungeonM=modelMatrix*instanceMatrix;vec3 dungeonO=(dungeonM*vec4(0.,0.,0.,1.)).xyz,dungeonAx=mat3(dungeonM)*vec3(.5,0.,0.),dungeonAz=mat3(dungeonM)*vec3(0.,0.,.5);
  vec3 dungeonA=length(dungeonAx)>length(dungeonAz)?dungeonAx:dungeonAz;
  if(abs(dqLink.w)>.001){dungeonO=(modelMatrix*vec4(dqLink.xyz,1.)).xyz;dungeonA=mat3(modelMatrix)*(dqLink.w>0.?vec3(dqLink.w,0.,0.):vec3(0.,0.,-dqLink.w));}
  vec4 dungeonP0=projectionMatrix*viewMatrix*vec4(dungeonO-dungeonA,1.),dungeonP1=projectionMatrix*viewMatrix*vec4(dungeonO+dungeonA,1.);
  vec2 dungeonQ0=((dungeonP0.xy/dungeonP0.w)*.5+.5-lwCutawayCenter.xy)/lwCutawayRadius,dungeonD=((dungeonP1.xy/dungeonP1.w)*.5+.5-lwCutawayCenter.xy)/lwCutawayRadius-dungeonQ0;
  float dungeonT=clamp(-dot(dungeonQ0,dungeonD)/max(dot(dungeonD,dungeonD),1e-6),0.,1.);
  if(length(dungeonQ0+dungeonD*dungeonT)<.9&&-(viewMatrix*vec4(dungeonO,1.)).z<lwCutawayCenter.z-.3&&length(dungeonA)>.45)gl_Position=vec4(0.,0.,-2.,1.);
}
#endif`;

const aperture = `
if(lwEligible>.5&&lwCutawayCenter.w>.5){
  vec2 screen=dungeonCutClip.xy/dungeonCutClip.w*.5+.5;
  float edge=length((screen-lwCutawayCenter.xy)/lwCutawayRadius);
  float aperture=1.-smoothstep(.70,1.,edge);
  aperture*=1.-smoothstep(lwCutawayCenter.z-.50,lwCutawayCenter.z-.20,lwViewDepth);
  if(aperture>.03)discard;
}`;

/** The cutaway hook of a surface material whose geometry carries `lwCutaway` and `dqLink`. */
export const cutawayHook: ShaderHook = {
  key: 'cutaway',
  uniforms,
  vertexHeader: 'attribute float lwCutaway;attribute vec4 dqLink;uniform vec4 lwCutawayCenter;uniform vec2 lwCutawayRadius;varying float lwEligible,lwViewDepth;varying vec4 dungeonCutClip;',
  fragmentHeader: 'uniform vec4 lwCutawayCenter;uniform vec2 lwCutawayRadius;varying float lwEligible,lwViewDepth;varying vec4 dungeonCutClip;',
  vertex: [{ chunk: 'project_vertex', after: collapse }],
  fragment: [{ chunk: 'alphatest_fragment', after: aperture }],
};

const center = new THREE.Vector3();
const head = new THREE.Vector3();
const foot = new THREE.Vector3();

/** Opens the aperture around the leader at world `(x, y, z)` (its body centre); `null` closes it. */
export const updateCutaway = (camera: THREE.PerspectiveCamera, leader: THREE.Vector3 | null): void => {
  const c = presentation.medium.cutaway;
  if (leader === null) {
    uniforms.lwCutawayCenter.value.w = 0;
    return;
  }
  const depth = -center.copy(leader).applyMatrix4(camera.matrixWorldInverse).z;
  head.copy(leader).setY(leader.y + c.reach).project(camera);
  foot.copy(leader).setY(leader.y - c.reach).project(camera);
  center.copy(leader).project(camera);
  const h = Math.abs(head.y - foot.y) * 0.5;
  uniforms.lwCutawayCenter.value.set(center.x * 0.5 + 0.5, center.y * 0.5 + 0.5, depth, 1);
  uniforms.lwCutawayRadius.value.set((h * c.width) / camera.aspect, h * c.height);
};

/**
 * The town's variant of the same hook (slice 6a, the engine's
 * `installArchitectureCutaway` with its default options): any geometry whose
 * `lwCutaway` vertex flag is set (the eastern house, `batch.ts`) opens in the
 * leader's ellipse, with no header collapse (the town has no frames) and a
 * 4 × 4 Bayer dither at the aperture's edge. Same uniforms, set by
 * `updateCutaway`.
 */
export const townCutawayHook: ShaderHook = {
  key: 'cutaway-town',
  uniforms,
  vertexHeader: 'attribute float lwCutaway;varying float lwEligible,lwViewDepth;varying vec4 townCutClip;',
  fragmentHeader: 'uniform vec4 lwCutawayCenter;uniform vec2 lwCutawayRadius;varying float lwEligible,lwViewDepth;varying vec4 townCutClip;',
  vertex: [{ chunk: 'project_vertex', after: 'townCutClip=gl_Position;lwEligible=lwCutaway;lwViewDepth=-mvPosition.z;' }],
  fragment: [
    {
      chunk: 'alphatest_fragment',
      after: `
if(lwEligible>.5&&lwCutawayCenter.w>.5){
  vec2 screen=townCutClip.xy/townCutClip.w*.5+.5;
  float edge=length((screen-lwCutawayCenter.xy)/lwCutawayRadius);
  float aperture=1.-smoothstep(.70,1.,edge);
  aperture*=1.-smoothstep(lwCutawayCenter.z-.50,lwCutawayCenter.z-.20,lwViewDepth);
  mat4 bayer=mat4(0.,8.,2.,10.,12.,4.,14.,6.,3.,11.,1.,9.,15.,7.,13.,5.);
  float threshold=(bayer[int(mod(gl_FragCoord.x,4.))][int(mod(gl_FragCoord.y,4.))]+.5)/16.;
  if(aperture>threshold)discard;
}`,
    },
  ],
};
